import { Loader2 } from 'lucide-react';
import { useEffect, useState, useCallback, useMemo, memo } from 'react';

import { cn } from 'lib/utils';

type Props = {
  loading: boolean;
  supplier?: string;
  LoadingType: 'upload' | 'check' | 'update' | 'main';
};

// Memoize static data
const istighfarVerses = [
  {
    verse:
      'وَاسْتَغْفِرُوا رَبَّكُمْ ثُمَّ تُوبُوا إِلَيْهِ إِنَّ رَبِّي رَحِيمٌ وَدُودٌ',
    reference: 'سورة هود - الآية 90',
  },
  {
    verse:
      'وَأَنِ اسْتَغْفِرُوا رَبَّكُمْ ثُمَّ تُوبُوا إِلَيْهِ يُمَتِّعْكُمْ مَتَاعًا حَسَنًا إِلَى أَجَلٍ مُسَمًّى',
    reference: 'سورة هود - الآية 3',
  },
  {
    verse: 'فَقُلْتُ اسْتَغْفِرُوا رَبَّكُمْ إِنَّهُ كَانَ غَفَّارًا',
    reference: 'سورة نوح - الآية 10',
  },
  {
    verse: 'وَاسْتَغْفِرِ اللَّهَ إِنَّ اللَّهَ كَانَ تَوَّابًا رَحِيمًا',
    reference: 'سورة النساء - الآية 106',
  },
];

// Memoized pattern overlay component
const PatternOverlay = memo(() => (
  <>
    {/* Pattern overlay - subtle dots */}
    <div
      className="absolute inset-0 opacity-[0.02] dark:opacity-[0.03]"
      style={{
        backgroundImage:
          'radial-gradient(rgba(0, 0, 0, 0.15) 1px, transparent 1px)',
        backgroundSize: '20px 20px',
      }}
    />

    {/* Gradient border */}
    <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-gray-200/10 via-gray-300/10 to-gray-200/10 dark:from-gray-800/10 dark:via-gray-700/10 dark:to-gray-800/10" />
  </>
));
PatternOverlay.displayName = 'PatternOverlay';

// Memoized spinner component
const Spinner = memo(({ isUpload }: { isUpload: boolean }) => (
  <div className="relative">
    <div className="h-20 w-20 animate-spin rounded-full border-4 border-gray-200/80 border-t-gray-600/80 dark:border-gray-800/80 dark:border-t-gray-400/80" />
    {isUpload && (
      <Loader2 className="absolute left-1/2 top-1/2 h-10 w-10 -translate-x-1/2 -translate-y-1/2 animate-spin text-gray-600/80 dark:text-gray-400/80" />
    )}
  </div>
));
Spinner.displayName = 'Spinner';

// Memoized verse component
const QuranVerse = memo(
  ({ verse, reference }: { verse: string; reference: string }) => (
    <div className="flex flex-col items-center gap-1">
      <div className="rounded-lg border border-indigo-200/50 bg-gradient-to-r from-indigo-50/50 to-purple-50/50 p-4 dark:border-indigo-800/50 dark:from-indigo-900/20 dark:to-purple-900/20">
        <p className="font-arabic text-center text-xl leading-relaxed text-indigo-600/90 dark:text-indigo-400/90">
          {verse}
        </p>
        <p className="mt-1 text-center text-sm font-medium text-indigo-500/70 dark:text-indigo-400/70">
          {reference}
        </p>
      </div>
    </div>
  ),
);
QuranVerse.displayName = 'QuranVerse';

// Memoized Islamic phrases component
const IslamicPhrases = memo(() => (
  <div className="flex flex-col items-center gap-2">
    <p className="font-arabic text-lg font-medium text-gray-700/90 dark:text-gray-300/90">
      لا إله إلا الله
    </p>
    <p className="font-arabic text-lg font-medium text-gray-700/90 dark:text-gray-300/90">
      اللهم صل على محمد وعلى آل محمد
    </p>
  </div>
));
IslamicPhrases.displayName = 'IslamicPhrases';

// Memoized progress dots component
const ProgressDots = memo(
  ({
    loadingType,
  }: {
    loadingType: 'upload' | 'check' | 'update' | 'main';
  }) => (
    <div className="flex space-x-3">
      <div
        className={`h-2.5 w-2.5 rounded-full ${loadingType === 'check' ? 'bg-gray-600/80 dark:bg-gray-400/80' : 'bg-gray-300/80 dark:bg-gray-700/80'} transition-colors duration-300`}
      />
      <div
        className={`h-2.5 w-2.5 rounded-full ${loadingType === 'upload' ? 'bg-gray-600/80 dark:bg-gray-400/80' : 'bg-gray-300/80 dark:bg-gray-700/80'} transition-colors duration-300`}
      />
      <div
        className={`h-2.5 w-2.5 rounded-full ${loadingType === 'update' ? 'bg-gray-600/80 dark:bg-gray-400/80' : 'bg-gray-300/80 dark:bg-gray-700/80'} transition-colors duration-300`}
      />
    </div>
  ),
);
ProgressDots.displayName = 'ProgressDots';

// Main component
const LoadingStage = ({ loading, supplier, LoadingType }: Props) => {
  const [mounted, setMounted] = useState(false);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [currentVerseIndex, setCurrentVerseIndex] = useState(0);

  // Mount effect
  useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);

  // Timer effect
  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (loading) {
      timer = setInterval(() => {
        setElapsedTime((prev) => prev + 1);
      }, 1000);
    } else {
      setElapsedTime(0);
    }
    return () => {
      if (timer) clearInterval(timer);
    };
  }, [loading]);

  // Verse rotation effect - only initialize once, not on every render
  useEffect(() => {
    const verseTimer = setInterval(() => {
      setCurrentVerseIndex((prev) => (prev + 1) % istighfarVerses.length);
    }, 10000); // Change verse every 10 seconds

    return () => clearInterval(verseTimer);
  }, []);

  // Memoized formatter function
  const formatTime = useCallback((seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
  }, []);

  // Memoized message based on LoadingType
  const message = useMemo(() => {
    if (LoadingType === 'check') {
      return '🔍 يتم مراجعة اصناف عرض الشراء و تحليل الاصناف و مقارنتها بالارصدة و خصومات السوق 📊';
    }
    if (LoadingType === 'upload') {
      return '📤 جاري الان رفع طلبية الشراء الي السيرفر ⏳';
    }
    if (LoadingType === 'update') {
      return '🔄 جاري تحديث البيانات 📝';
    }
    return '';
  }, [LoadingType]);

  // Memoize current verse to prevent unnecessary re-renders
  const currentVerse = useMemo(
    () => istighfarVerses[currentVerseIndex],
    [currentVerseIndex],
  );

  // Return null early if not loading - performance optimization
  if (!loading) return null;

  return (
    <div
      className={cn(
        'fixed inset-0 z-[5000] flex flex-col items-center justify-center bg-gradient-to-br from-white/60 via-white/70 to-white/60 backdrop-blur-sm transition-all duration-500 dark:from-black/60 dark:via-black/70 dark:to-black/60',
        mounted ? 'opacity-100' : 'opacity-0',
      )}
    >
      <div className="relative w-full max-w-2xl rounded-2xl border-2 border-gray-200/90 bg-white/80 p-8 shadow-[0_8px_30px_rgb(0,0,0,0.08)] ring-1 ring-gray-300/50 dark:border-gray-700/90 dark:bg-gray-900/80 dark:ring-gray-600/50">
        <PatternOverlay />

        <div className="relative flex flex-col items-center justify-center space-y-8">
          {/* Islamic Phrases */}
          <div className="mt-6 flex flex-col items-center gap-3">
            {/* Quranic Verse */}
            <QuranVerse
              verse={currentVerse.verse}
              reference={currentVerse.reference}
            />

            {/* Tawheed and Salawat */}
            <IslamicPhrases />
          </div>
          {/* Spinner */}
          <Spinner isUpload={LoadingType === 'upload'} />

          {/* Content */}
          <div className="flex flex-col items-center text-center">
            {/* Supplier name */}
            {supplier && (
              <div className="mb-2 flex flex-col items-center">
                <h3 className="text-3xl font-bold text-gray-800/90 dark:text-gray-200/90">
                  {supplier}
                </h3>
                <div className="mt-1 h-1 w-24 rounded-full bg-gradient-to-r from-indigo-500/20 to-purple-500/20 dark:from-indigo-400/20 dark:to-purple-400/20" />
              </div>
            )}

            {/* Message */}
            {message && (
              <p className="mt-3 max-w-xl text-base font-medium text-gray-600/80 dark:text-gray-400/80">
                {message}
              </p>
            )}

            {/* Timer */}
            <div className="mt-6 flex items-center gap-2 text-sm font-medium text-gray-500/80 dark:text-gray-400/80">
              <span>⏱️ الوقت المستغرق:</span>
              <span className="font-mono">{formatTime(elapsedTime)}</span>
            </div>
          </div>

          {/* Progress dots */}
          <ProgressDots loadingType={LoadingType} />
        </div>
      </div>
    </div>
  );
};

export default memo(LoadingStage);
